import type { OverviewClip } from './conversations';
import { axiosInstance } from './api';

/** Чеки из ОФД (Алексей 22.10, #1941) — BE plugins/ops_tasks/api/receipts.py.
 *  Ключи подключения только отправляются: в ответах лишь `has_secrets`. */
export type ReceiptProvider = 'saby';

export interface ReceiptRegisterDto {
  id: number;
  reg_id: string;
  sales_point: string;
  address: string;
  model: string;
  zone_id: number | null;
  zone_name: string | null;
  synced_until: string | null;
}

export interface ReceiptConnectionDto {
  id: number;
  provider: ReceiptProvider;
  name: string;
  inn: string;
  /** Юрлицо группы филиалов (#2010); null — старое подключение без ЮЛ. */
  legal_entity: { id: number; name: string; inn: string; group: { id: number; name: string } } | null;
  enabled: boolean;
  has_secrets: boolean;
  last_sync_at: string | null;
  last_error: string;
  receipts: number;
  registers: ReceiptRegisterDto[];
}

export interface SabySecrets {
  app_client_id: string;
  app_secret: string;
  secret_key: string;
}

export interface ReceiptsSummaryDto {
  connected: boolean;
  date_from: string;
  date_to: string;
  count: number;
  returns: number;
  revenue: number;
  avg_check: number | null;
  by_day: { date: string; count: number; revenue: number; avg_check: number | null }[];
  by_hour: { hour: number; count: number }[];
  top_items: { name: string; qty: number; revenue: number }[];
  operators: { name: string; count: number; revenue: number }[];
  last_receipt_at: string | null;
}

/** Подключение = юрлицо в ОФД (#1985); список — те, что пользователь может настраивать (#2003). */
export const receiptConnectionsKey = ['receipt-connections'] as const;

export const listReceiptConnections = () =>
  axiosInstance.get<{ connections: ReceiptConnectionDto[]; can_add: boolean }>('/ops_tasks/receipts/connections/');

export const createReceiptConnection = (body: { legal_entity_id?: number; zone_id?: number; provider: ReceiptProvider; name: string; secrets: SabySecrets }) =>
  axiosInstance.post<ReceiptConnectionDto>('/ops_tasks/receipts/connections/', body);

export const updateReceiptConnection = (
  id: number,
  body: Partial<{ name: string; enabled: boolean; secrets: SabySecrets; registers: { id: number; zone_id: number | null }[] }>,
) => axiosInstance.patch<ReceiptConnectionDto>(`/ops_tasks/receipts/connections/${id}/`, body);

export const deleteReceiptConnection = (id: number) => axiosInstance.delete(`/ops_tasks/receipts/connections/${id}/`);

export const syncReceiptConnection = (id: number) => axiosInstance.post(`/ops_tasks/receipts/connections/${id}/sync/`);

export const getReceiptsSummary = (zoneId: string, dateFrom?: string, dateTo?: string) =>
  axiosInstance.get<ReceiptsSummaryDto>(`/ops_tasks/receipts/${zoneId}/summary/`, {
    params: { date_from: dateFrom, date_to: dateTo },
  });

/** Данные → Продажи позиций (#3181): когда позицию перестают брать и сколько недопродали. Минуты — от полуночи. */
export interface ItemGapDto {
  day: string; from: number; to: number; expected_qty: number; expected_rub: number;
  /** ждали чеков с позицией; хвост дня; вопросов гостя в окне; «точно» или «вероятно» (#3201) */
  expected_checks?: number; tail?: boolean; asked?: number; sure?: boolean;
}
export interface ItemGapsItemDto {
  name: string;
  category: string;
  drink: boolean;
  qty: number;
  days_sold: number;
  avg_price: number;
  last_median: number | null;
  last_swing: number | null;
  lost_qty: number;
  lost_rub: number;
  /** Оценка сверху (#3199): хвост вечера по «полным» дням, когда позиция была до закрытия */
  full_days?: number;
  upper_qty?: number | null;
  upper_rub?: number | null;
  /** На 100 чеков: своя точка и середина остальных точек бренда (#3201) */
  network?: { own: number; median: number; peers: number; gap_qty: number; gap_rub: number };
  by_day: Record<string, { last: number | null; lost_rub: number; qty: number }>;
}
export interface ItemGapsDto {
  date_from: string;
  date_to: string;
  days: { day: string; open: number; close: number }[];
  items: ItemGapsItemDto[];
  detail?: {
    /** у суммы позиций — «A + B», а сами позиции в items (Алексей 03.11: поправка на каннибализацию) */
    name: string;
    items?: string[];
    sales: Record<string, number[]>;
    gaps: ItemGapDto[];
    profile: { bin: number; share: number }[];
    /** день → час → штук (#3186) */
    hourly: Record<string, Record<string, number>>;
    racks: { id: number; name: string; rack_type: string }[];
    default_rack: number | null;
    rack: number | null;
    /** день → час → средняя заполненность %, доля проблемных оценок, оценок */
    layout: Record<string, Record<string, { pct: number; bad: number; n: number }>> | null;
    /** день → час → разговоров / с апселлом / предлагали эту позицию (#3279); нет микрофона — нет поля */
    upsell?: Record<string, Record<string, { n: number; any: number; offer: number }>>;
    /** слово позиции, по которому искали «что предлагали» («хот-дог») */
    offer_word?: string;
    /** «Гость спрашивал» по звуку (#3186): минута от полуночи */
    /** clip — кусок диалога со звуком для ClipExample (#3243). */
    asks?: { day: string; minute: number; asked: string; answer: string; conversation_id: number; clip?: OverviewClip | null }[];
  } | null;
  total_lost_rub: number;
  total_upper_rub?: number;
  /** «Только напиток» (#3204): день → час → [чеков без еды с напитком, всех чеков] */
  drink_only?: Record<string, Record<string, [number, number]>>;
  /** По дням (#3209): чеков, чеков с едой, разговоров и доля апселла (null — мало разговоров или нет звука) */
  daily?: {
    day: string; checks: number; food_checks: number; conversations?: number; upsell_share?: number | null;
    /** категория еды → [шт, ₽] (#3210); item — выбранная позиция [шт, ₽] */
    cats?: Record<string, [number, number]>; item?: [number, number] | null;
  }[];
  bin_min: number;
  min_expected: number;
}

/** item — позиция или несколько (?item=A&item=B — их продажи складываются). */
export const getItemGaps = (zoneId: string, dateFrom?: string, dateTo?: string, item?: string | string[] | null, rack?: number | null, by?: 'category' | null) => {
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  for (const n of Array.isArray(item) ? item : item ? [item] : []) params.append('item', n);
  if (rack) params.set('rack', String(rack));
  if (by) params.set('by', by);
  return axiosInstance.get<ItemGapsDto>(`/ops_tasks/receipts/${zoneId}/item-gaps/`, { params });
};

/** Средний чек сегодня к этому часу против 14 прошлых дней в те же часы (#2143). */
export interface ReceiptsTodayDto {
  connected: boolean;
  count?: number;
  avg_check?: number | null;
  baseline_avg_check?: number | null;
  /** День недели базы (0 = вс): в праздник сравниваем с воскресеньями (#3989). */
  baseline_dow?: number;
  holiday?: boolean;
  baseline_days?: number;
  change?: number | null;
  tone?: 'ok' | 'attention' | 'warning' | 'critical' | 'none';
  /** «Обычно в это время берут…» (#2384): ходовая еда ±1 ч в такие же дни, доля чеков с ней. */
  tip?: { name: string; share: number } | null;
  /** До 2 ходовых позиций (#2456): today — продано сегодня к этому часу, usual — обычно к этому часу. */
  tips?: { name: string; share: number; today: number; usual: number }[];
  /** Средний чек по местным часам сегодня, от открытия до текущего часа (#8817); пустой час — null. */
  hourly?: ReceiptsHourDto[];
  /** То же в такие же дни за 4 прошлые недели: count — в среднем за день. */
  baseline_hourly?: ReceiptsHourDto[];
}

export interface ReceiptsHourDto { hour: number; avg_check: number | null; count: number }

export const getReceiptsToday = (zoneId: string) =>
  axiosInstance.get<ReceiptsTodayDto>(`/ops_tasks/receipts/${zoneId}/today/`);

export interface ReceiptProductDto {
  name: string;
  category: string;
  source: 'auto' | 'manual';
  qty_30d: number;
}

export interface ReceiptProductsDto {
  categories: { key: string; label: string; rack_type: string | null }[];
  products: ReceiptProductDto[];
}

export const receiptProductsKey = (connId: number) => ['receipt-products', connId] as const;

export const getReceiptProducts = (connId: number) =>
  axiosInstance.get<ReceiptProductsDto>(`/ops_tasks/receipts/connections/${connId}/products/`);

export const setReceiptProductCategories = (connId: number, items: { name: string; category: string }[]) =>
  axiosInstance.patch<ReceiptProductsDto>(`/ops_tasks/receipts/connections/${connId}/products/`, { items });

/** Веса разделов в рейтинге филиалов группы (#2400): пусто — значения по умолчанию на фронте. */
export interface StatusWeightsDto { weights: Record<string, number>; can_edit: boolean }
export const getStatusWeights = (groupId: string) =>
  axiosInstance.get<StatusWeightsDto>(`/ops_tasks/status-weights/${groupId}/`);
export const putStatusWeights = (groupId: string, weights: Record<string, number>) =>
  axiosInstance.put<StatusWeightsDto>(`/ops_tasks/status-weights/${groupId}/`, { weights });

/** Лента чеков (Алексей 27.10 #2962): новые сверху, курсор — следующая страница. Суммы в рублях. */
/** Откуда позиция (#2962): сам назвал / после «что-то ещё?» / порекомендовал / прямо допродал. */
export type ReceiptItemSource = 'self' | 'after_ask' | 'recommended' | 'upsold';

export interface ReceiptItemDto {
  name: string;
  qty: number;
  price: number;
  sum: number;
  src?: { label: ReceiptItemSource; quote: string } | null;
}

export interface ReceiptConversationDto {
  id: number;
  match: 'high' | 'medium';
  started_at: string;
  summary: string;
  dialogue: string;
}

export interface ReceiptRowDto {
  id: number;
  at: string;
  doc: number;
  op: 'sale' | 'return';
  total: number;
  card: boolean;
  cash: boolean;
  operator: string;
  shift: number | null;
  items: ReceiptItemDto[];
  conversation?: ReceiptConversationDto;
  /** Предложил кассир, гость не взял (#3229). */
  /** generic — общее «что-нибудь ещё?» без конкретной позиции (#3465). */
  declined?: { what: string; quote: string; generic?: boolean }[];
  /** Просил гость, а не оказалось: sold_out — кончился, not_delivered — не привезли, absent — нет, not_sold_here — не продаём. */
  missing?: { what: string; answer: string; quote: string; reason: 'sold_out' | 'not_delivered' | 'absent' | 'not_sold_here' }[];
  /** Пометки момента: витрина не полная, длинная очередь. */
  /** low_layout.level — степень недостатка (#3469): slight / medium / strong; deficit — доля порога. */
  marks?: ({ kind: 'low_layout'; rack: string; level?: 'slight' | 'medium' | 'strong'; deficit?: number } | { kind: 'queue'; people: number })[];
}

export interface ReceiptsPageDto {
  results: ReceiptRowDto[];
  next_cursor: string | null;
  /** Сдвиг часов кассы относительно звука по дням: чек − оплата, секунды; warn — больше 2 мин. */
  clock?: Record<string, { offset_s: number | null; n: number; warn: boolean }>;
}

export const listReceipts = (zoneId: string, params: { cursor?: string | null; operator?: string; date_from?: string; date_to?: string; limit?: number }) =>
  axiosInstance.get<ReceiptsPageDto>(`/ops_tasks/receipts/${zoneId}/list/`, {
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')),
  });

/** Речь → явные недопродажи (#3190): «нет товара» из разговоров × цена позиции × чек разговора. */
export type LostOutcome = 'left' | 'downgraded' | 'replaced' | 'other' | 'no_receipt';
export interface LostSaleRowDto {
  conversation_id: number;
  started_at: string;
  day: string;
  asked: string;
  answer: string;
  severity: string | null;
  item: string | null;
  price: number | null;
  receipt_id: number | null;
  outcome: LostOutcome;
  /** почему не было (#3212): кончился сегодня / не привезли-не приготовили / нет / не продаём */
  reason?: 'sold_out' | 'not_delivered' | 'absent' | 'not_sold_here';
  substitute: string | null;
  substitute_price: number | null;
  lost_rub: number;
}
export interface LostSalesDto {
  date_from: string;
  date_to: string;
  total_lost_rub: number;
  maybe_rub: number;
  /** Явные допродажи по голосу (#3567): позиции связанных чеков, которые кассир предложил и гость взял. */
  upsell?: { upsold_rub: number; upsold_n: number; recommended_rub: number; recommended_n: number; linked: number };
  by_outcome: Partial<Record<LostOutcome, number>>;
  by_reason?: Partial<Record<'sold_out' | 'not_delivered' | 'absent' | 'not_sold_here', number>>;
  by_item: { item: string | null; asked: string; count: number; lost_rub: number }[];
  rows: LostSaleRowDto[];
}
export const getLostSales = (zoneId: string, dateFrom?: string, dateTo?: string) =>
  axiosInstance.get<LostSalesDto>(`/ops_tasks/receipts/${zoneId}/lost-sales/`, { params: { date_from: dateFrom, date_to: dateTo } });
